import appConfig from '../../config/appConfig';
import * as firmwareErrorCodes from '../../consts/iot/firmwareErrorCodes';
import * as localizationKeys from '../../consts/localization/localizationKeys';
import {ENABLE_FW_UPDATES_CHECK} from '../../consts/tnc/aggregatedSettingTypes';
import ModalTypes from '../../enums/app/modalTypes';
import {setNotificationSuccess} from '../../state/ducks/global';
import {setFWUSuccess} from '../../state/ducks/iotDevice/actions';
import {selectFirmwareFwuTarget} from '../../state/selectors/iotDevice';
import {selectMwDeviceFirmwareData} from '../../state/selectors/mwDevice';
import {dispatch, getState} from '../../state/store';
import converter from '../../utils/converter';
import FwuDownloadOrParseError from '../errors/firmware/fwuDownloadOrParseError';
import FwuDownloadUrlIsEmptyError from '../errors/firmware/fwuDownloadUrlIsEmptyError';
import fwuPackageService from '../fwu/fwuPackageService';
import mwMessageRequestService from '../iot/mwMessageRequestService';
import {getLocalizedStrings} from '../localization/localizationService';
import log from '../logger/log';
import modalService from '../modalService';
import appRouterService from '../route/appRouterService';
import server from '../server/server';
import tncService from '../tncService';
import connectivityService from './connectivityService';
import fwuClientService from './fwuClientService';
import fwuTargetService from './fwuTargetService';

let fwuPackage = undefined;

const clearPackageData = () => {
    fwuPackage = null;
};

const downloadFwuPackage = async (fwu_target, onSuccess, onFwuError) => {
    try {
        if (fwuPackage) return true;

        await downloadFirmware(fwu_target, onSuccess);

        return true;
    } catch (e) {
        log.error(`FWUService: FWU package downloading is failed for fwuTarget: ${fwu_target}, ${e}`);

        mwMessageRequestService.onFwuErrorHandler({errorCode: e.code, onError: onFwuError, fwu_target});

        return false;
    }
};

const downloadFirmware = async (fwu_target, onSuccess) => {
    const firmwareData = selectMwDeviceFirmwareData(getState());
    const firmwareTarget = fwuTargetService.getIotTargetByFwuTarget(firmwareData, fwu_target);
    const {download_url} = firmwareTarget || {};

    if (!download_url) throw new FwuDownloadUrlIsEmptyError();

    try {
        const res = await server.get(download_url);

        fwuPackage = res.data;

        log.info(`FWUService: FWU package for fwuTarget: ${fwu_target} has been downloaded successfully`);

        mwMessageRequestService.onPackageLoaded(onSuccess, fwu_target);
    } catch (e) {
        throw new FwuDownloadOrParseError(e);
    }
};

const startFwUpdate = async ({onFwuError, onFwuStatusEvent, onFwuFinish, package_index}) => {
    const fwu_target = selectFirmwareFwuTarget(getState());

    if (fwuPackage) {
        log.info(`FWUService: start FWU update process. fwuTarget: ${fwu_target}`);

        const FwuClient = fwuClientService.getFwuClient(fwu_target);
        const options = {
            fwu_target,
            fwuPackageData: fwuPackage,
            onFwuError,
            onFwuFinish,
            onFwuStatusEvent,
        };
        const fwuClient = new FwuClient(options);

        fwuClient.startFwUpdate(package_index);
    } else {
        mwMessageRequestService.onFwuErrorHandler({
            errorCode: firmwareErrorCodes.START_NOT_FOUND_DATA,
            fwu_target,
            onError: onFwuError,
        });

        log.info(`FWUService: startFwUpdate error, package is not loaded. fwuTarget: ${fwu_target}`);
    }
};

const getFwUpdateFinishAfterRebootEvent = () => fwuPackage?.reboot_after_fwu_message;

const convertHexIndexToInt = (hex) => {
    const hexIndexReversed = hex
        .match(/.{1,2}/g)
        .reverse()
        .join('');
    const index = converter.hex2dec(hexIndexReversed);

    return parseInt(index);
};

const isFWUAvailable = (type) => {
    if (!type) return false;

    const isUsb = connectivityService.isUsb();
    const isUsbFWUAvailable = appConfig.getIsUsbFWUAvailable(type);
    const isBleFWUAvailable = appConfig.getIsBleFWUAvailable(type);
    const isAnyFWUAvailable = isUsbFWUAvailable || isBleFWUAvailable;

    return isUsb ? isUsbFWUAvailable : isAnyFWUAvailable;
};

const initiateUpdateFirmwareMessage = (target) => {
    const fwuInfo = fwuPackageService.getFwuInfoByFwuTargetStatic(target);
    const version = fwuInfo?.availableFirmwares ? fwuInfo?.availableFirmwares[0]?.version : undefined;

    // hide some modals from background (e.g. reset failed)
    modalService.hideModal(ModalTypes.FWU_ERROR);

    appRouterService.forwardToUpdateFWPage({
        target,
        version,
        isSblFirmware: fwuInfo?.isSblFirmware,
    });
};

const getIsBackgroundFwuCheckEnabled = () => {
    const isBackgroundFwuCheckEnabled =
        appConfig.getIsBackgroundFwuCheckEnabled() && tncService.isAggregatedSettingEnabled(ENABLE_FW_UPDATES_CHECK);

    return isBackgroundFwuCheckEnabled;
};

const onFwuSuccess = () => {
    log.info('End firmware process. Success notification is displayed.');
    dispatch(setNotificationSuccess(getLocalizedStrings()[localizationKeys.FIRMWARE_UPDATE_SUCCESS]));
    dispatch(setFWUSuccess());
};

export default {
    clearPackageData,
    convertHexIndexToInt,
    downloadFirmware,
    downloadFwuPackage,
    getFwUpdateFinishAfterRebootEvent,
    getIsBackgroundFwuCheckEnabled,
    initiateUpdateFirmwareMessage,
    isFWUAvailable,
    onFwuSuccess,
    startFwUpdate,
};
